import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { snapTimeStringToQuarterHour } from "../utils/timeRounding";

const HOURS_12 = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];

const MINUTES_15 = ["00", "15", "30", "45"];
const MERIDIEMS = ["AM", "PM"];

function toParts(value) {
  if (!value || typeof value !== "string") {
    return { hour12: "09", minute: "00", meridiem: "AM" };
  }

  const snapped = snapTimeStringToQuarterHour(value);
  const [hStr, mStr] = snapped.split(":");
  let hours24 = parseInt(hStr, 10);
  let minutes = parseInt(mStr, 10);

  if (Number.isNaN(hours24)) hours24 = 9;
  if (Number.isNaN(minutes)) minutes = 0;

  const meridiem = hours24 >= 12 ? "PM" : "AM";
  let hour12 = hours24 % 12;
  if (hour12 === 0) hour12 = 12;

  return {
    hour12: String(hour12).padStart(2, "0"),
    minute: String(minutes).padStart(2, "0"),
    meridiem,
  };
}

function fromParts(hour12Str, minuteStr, meridiem) {
  let hour12 = parseInt(hour12Str, 10);
  let minutes = parseInt(minuteStr, 10);

  if (Number.isNaN(hour12)) hour12 = 9;
  if (Number.isNaN(minutes)) minutes = 0;

  let hours24 = hour12 % 12;
  if (meridiem === "PM") {
    hours24 += 12;
  }

  return `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function formatDisplay(value) {
  if (!value) return "";
  const { hour12, minute, meridiem } = toParts(value);
  return `${hour12}:${minute} ${meridiem}`;
}

function parseTypedValue(input) {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim().toUpperCase();
  const match = trimmed.match(/^(\d{1,2})(?::(\d{1,2}))?\s*([AP]M)?$/);
  if (!match) return null;

  const hourRaw = parseInt(match[1], 10);
  const minuteRaw = match[2] != null ? parseInt(match[2], 10) : 0;
  const meridiem = match[3] || null;

  if (
    Number.isNaN(hourRaw) ||
    Number.isNaN(minuteRaw) ||
    minuteRaw < 0 ||
    minuteRaw > 59
  ) {
    return null;
  }

  let hours24 = hourRaw;
  if (meridiem) {
    if (hourRaw < 1 || hourRaw > 12) return null;
    hours24 = hourRaw % 12;
    if (meridiem === "PM") hours24 += 12;
  } else if (hourRaw < 0 || hourRaw > 23) {
    return null;
  }

  return `${String(hours24).padStart(2, "0")}:${String(minuteRaw).padStart(2, "0")}`;
}

export default function TimePickerPopover({
  value,
  onChange,
  className = "",
  inputClassName = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatDisplay(value));
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const parts = useMemo(() => toParts(value), [value]);

  useEffect(() => {
    setInputValue(formatDisplay(value));
  }, [value]);

  useEffect(() => {
    if (!open || disabled) return;
    if (!value) return;
    const snapped = snapTimeStringToQuarterHour(value);
    if (snapped !== value) {
      onChange?.(snapped);
    }
  }, [open, value, disabled, onChange]);

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };

    const handlePointerDown = (event) => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;
      if (anchor.contains(event.target) || popover.contains(event.target)) return;
      setOpen(false);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [open]);

  const applyTypedValue = () => {
    if (!inputValue) {
      setInputValue(formatDisplay(value));
      return;
    }
    const parsed = parseTypedValue(inputValue);
    if (!parsed) {
      setInputValue(formatDisplay(value));
      return;
    }
    const snapped = snapTimeStringToQuarterHour(parsed);
    onChange?.(snapped);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyTypedValue();
      setOpen(false);
    }
  };

  const handleSelect = (nextHour12, nextMinute, nextMeridiem) => {
    const next = fromParts(nextHour12, nextMinute, nextMeridiem);
    onChange?.(snapTimeStringToQuarterHour(next));
  };

  return (
    <div className={`relative ${className}`} ref={anchorRef}>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onBlur={applyTypedValue}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-12 text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-200 ${inputClassName}`}
          placeholder="HH:MM AM"
        />
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          disabled={disabled}
          className="absolute inset-y-0 right-3 my-auto h-8 w-8 rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          aria-label="Open time picker"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 mx-auto"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M12 7v5l3 2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="rounded-2xl border border-slate-200 bg-white shadow-xl"
            style={{
              position: "absolute",
              top: popoverPosition.top,
              left: popoverPosition.left,
              width: popoverPosition.width,
              zIndex: 12050,
            }}
          >
            <div className="grid grid-cols-3 gap-2 p-3">
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  Hour
                </div>
                <div className="max-h-52 overflow-y-auto rounded-xl border border-slate-100">
                  {HOURS_12.map((hour) => (
                    <button
                      key={hour}
                      type="button"
                      onClick={() =>
                        handleSelect(hour, parts.minute, parts.meridiem)
                      }
                      className={`w-full px-3 py-2 text-left text-sm ${
                        parts.hour12 === hour
                          ? "bg-rose-500 text-white"
                          : "text-slate-700 hover:bg-rose-50"
                      }`}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  Minute
                </div>
                <div className="max-h-52 overflow-y-auto rounded-xl border border-slate-100">
                  {MINUTES_15.map((minute) => (
                    <button
                      key={minute}
                      type="button"
                      onClick={() =>
                        handleSelect(parts.hour12, minute, parts.meridiem)
                      }
                      className={`w-full px-3 py-2 text-left text-sm ${
                        parts.minute === minute
                          ? "bg-rose-500 text-white"
                          : "text-slate-700 hover:bg-rose-50"
                      }`}
                    >
                      {minute}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  AM/PM
                </div>
                <div className="max-h-52 overflow-y-auto rounded-xl border border-slate-100">
                  {MERIDIEMS.map((meridiem) => (
                    <button
                      key={meridiem}
                      type="button"
                      onClick={() =>
                        handleSelect(parts.hour12, parts.minute, meridiem)
                      }
                      className={`w-full px-3 py-2 text-left text-sm ${
                        parts.meridiem === meridiem
                          ? "bg-rose-500 text-white"
                          : "text-slate-700 hover:bg-rose-50"
                      }`}
                    >
                      {meridiem}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
